import { FC, HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlusOutlined } from '@ant-design/icons';

import type { UploadFile, UploadProps } from 'antd';
import { Image, Upload } from 'antd';

import { useDeleteImageMutation, useSourceSlice } from 'entities/Source';
import { FileType } from 'shared/typings';
import { getBase64, getCookie } from 'shared/utils';

interface IArbitrationImageUploadProps extends HTMLAttributes<HTMLDivElement> {
	disabled?: boolean;
	screenshots: string[];
}

export const ArbitrationImageUpload: FC<IArbitrationImageUploadProps> = ({ disabled, screenshots }) => {
	const { t } = useTranslation();

	const [deleteImage] = useDeleteImageMutation();
	const { arbitrationValues, setArbitrationValues } = useSourceSlice();

	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState('');
	const [fileList, setFileList] = useState<UploadFile[]>(
		screenshots.map((uuid) => ({
			uid: uuid,
			url: `/api/arbitrage_screenshots/${uuid}/`,
			name: uuid,
		})),
	);

	const handlePreview = async (file: UploadFile) => {
		if (!file.url && !file.preview) {
			file.preview = await getBase64(file.originFileObj as FileType);
		}

		setPreviewImage(file.url || (file.preview as string));
		setPreviewOpen(true);
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		if (info.file.status === 'done') {
			info.file.uid = info.file.response.uuid;
		}

		setFileList(info.fileList);
		setArbitrationValues({ ...arbitrationValues, screenshots: info.fileList.map((file) => file.uid) });
	};

	const handleRemove: UploadProps['onRemove'] = (file) => {
		deleteImage(file.uid);
	};

	const uploadButton = (
		<button style={{ border: 0, background: 'none' }} type="button">
			<PlusOutlined />
			<div style={{ marginTop: 8 }}>{t('Загрузить')}</div>
		</button>
	);

	return (
		<>
			<Upload
				name="image"
				accept="image/png, image/jpeg"
				action="/api/arbitrage_screenshots/"
				headers={{ 'X-Csrftoken': getCookie('csrftoken') }}
				listType="picture-card"
				fileList={fileList}
				onPreview={handlePreview}
				onChange={handleChange}
				onRemove={handleRemove}
				disabled={disabled}>
				{fileList.length >= 8 || disabled ? null : uploadButton}
			</Upload>
			{previewImage && (
				<Image
					wrapperStyle={{ display: 'none' }}
					preview={{
						visible: previewOpen,
						onVisibleChange: (visible) => setPreviewOpen(visible),
						afterOpenChange: (visible) => !visible && setPreviewImage(''),
					}}
					src={previewImage}
				/>
			)}
		</>
	);
};
